import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Award, Sparkles, Briefcase, Cpu } from 'lucide-react';
import CERTS from '../data/certificates.json';
import useMediaQuery, { PHONE } from '../hooks/useMediaQuery';

const EASE = [0.16, 1, 0.3, 1];
const PREVIEW = 4;

const SIMULATION_ISSUERS = ['EY', 'British Airways', 'KPMG', 'J.P. Morgan', 'Goldman Sachs'];

const ISSUER_META = {
  Anthropic: { abbr: 'A', accent: '#D97757', domain: 'ai' },
  Google: { abbr: 'G', accent: '#4285F4', domain: 'data' },
  'Job Simulations': { abbr: 'JS', accent: '#10B981', domain: 'finance' },
  'Amazon Web Services (AWS)': { abbr: 'AWS', accent: '#FF9900', domain: 'ai', label: 'Amazon Web Services' },
  'Udacity × AWS': { abbr: 'U', accent: '#02B3E4', domain: 'ai' },
  IBM: { abbr: 'IBM', accent: '#4589FF', domain: 'ai' },
  Bloomberg: { abbr: 'B', accent: '#F26522', domain: 'finance' },
  'McKinsey & Company': { abbr: 'M', accent: '#2251FF', domain: 'finance' },
  LinkedIn: { abbr: 'in', accent: '#0A66C2', domain: 'ai', label: 'LinkedIn Learning' },
  BDO: { abbr: 'BDO', accent: '#E81A3B', domain: 'finance' },
  'Loughborough University': { abbr: 'LU', accent: '#8B5CF6', domain: 'academic' },
  'University of Westminster': { abbr: 'UW', accent: '#A855F7', domain: 'academic' },
};

const DOMAINS = [
  { id: 'all', label: 'All' },
  { id: 'ai', label: 'AI & Cloud' },
  { id: 'data', label: 'Data & Analytics' },
  { id: 'finance', label: 'Finance & Consulting' },
  { id: 'academic', label: 'Academic' },
];

const groupKey = (c) => {
  if (SIMULATION_ISSUERS.includes(c.issuer)) return 'Job Simulations';
  if (c.issuer === 'Udacity') return 'Udacity × AWS';
  return c.issuer;
};

const GROUPS = Object.values(
  CERTS.reduce((acc, c) => {
    const key = groupKey(c);
    const meta = ISSUER_META[key] || { abbr: key[0], accent: '#9B3FFE', domain: 'ai' };
    acc[key] = acc[key] || { key, ...meta, label: meta.label || key, certs: [] };
    acc[key].certs.push(c);
    return acc;
  }, {})
).sort((a, b) => b.certs.length - a.certs.length);

const ISSUER_COUNT = new Set(CERTS.map((c) => c.issuer)).size;
const count = (issuer) => CERTS.filter((c) => c.issuer === issuer).length;

const HIGHLIGHTS = [
  {
    icon: Award,
    accent: '#2251FF',
    kicker: 'McKinsey & Company · Jul 2026',
    title: 'McKinsey Forward',
    body: 'Leadership and consulting programme. Certificate and badge issued on completion.',
  },
  {
    icon: Sparkles,
    accent: '#FF9900',
    kicker: 'Udacity × AWS · Mar 2026',
    title: 'AWS AI & ML Scholar',
    body: '2026 challenge completion, AI Practitioner challenge and two project badges.',
  },
  {
    icon: Cpu,
    accent: '#D97757',
    kicker: `Anthropic · ${count('Anthropic')} courses`,
    title: 'Claude & Agentic AI',
    body: 'Claude API, Model Context Protocol, Claude Code, subagents, agent skills, Bedrock and Vertex AI.',
  },
  {
    icon: Briefcase,
    accent: '#10B981',
    kicker: 'Forage · Apr 2026',
    title: 'Six Job Simulations',
    body: 'J.P. Morgan quant research, Goldman Sachs, EY ×2, KPMG audit and British Airways data science.',
  },
];

/* ─── Issuer group card ───────────────────────────────────────────────── */
const GroupCard = ({ group, preview }) => {
  const [open, setOpen] = useState(false);
  const rows = open ? group.certs : group.certs.slice(0, preview);
  const hidden = group.certs.length - preview;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.45, ease: EASE }}
      className="rounded-2xl border border-white/[0.07] bg-[rgba(12,12,16,0.7)] overflow-hidden break-inside-avoid mb-4"
    >
      <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/[0.06]">
        <div
          className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-[9.5px] font-bold tracking-wide"
          style={{ background: `${group.accent}18`, color: group.accent, border: `1px solid ${group.accent}33` }}
        >
          {group.abbr}
        </div>
        <span className="text-[12.5px] font-medium text-white/85">{group.label}</span>
        <span
          className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium tabular-nums"
          style={{ color: group.accent, background: `${group.accent}14` }}
        >
          {group.certs.length}
        </span>
      </div>

      <ul>
        {rows.map((cert) => (
          <li
            key={cert.name + cert.date}
            className="flex items-start justify-between gap-3 px-4 py-3 border-b border-white/[0.045] last:border-b-0 hover:bg-white/[0.02] transition-colors duration-150"
          >
            <div className="min-w-0">
              <p className="text-[12.5px] text-white/80 leading-snug">{cert.name}</p>
              {(cert.id || group.key === 'Job Simulations') && (
                <p className="text-[10px] text-text-secondary mt-1 font-mono truncate">
                  {group.key === 'Job Simulations' && <span className="font-body not-italic mr-1.5">{cert.issuer}</span>}
                  {cert.id}
                </p>
              )}
            </div>
            <span className="text-[10.5px] text-text-secondary flex-shrink-0 mt-0.5 tabular-nums">{cert.date}</span>
          </li>
        ))}
      </ul>

      {hidden > 0 && (
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-[11.5px] font-medium text-text-secondary hover:text-white border-t border-white/[0.06] hover:bg-white/[0.02] transition-colors"
        >
          {open ? 'Show less' : `Show ${hidden} more`}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </button>
      )}
    </motion.div>
  );
};

/* ─── Section block ───────────────────────────────────────────────────── */
const Certifications = () => {
  const [domain, setDomain] = useState('all');
  const [allGroups, setAllGroups] = useState(false);
  const isPhone = useMediaQuery(PHONE);
  const groups = useMemo(
    () => (domain === 'all' ? GROUPS : GROUPS.filter((g) => g.domain === domain)),
    [domain]
  );
  const domainCount = (id) =>
    (id === 'all' ? GROUPS : GROUPS.filter((g) => g.domain === id)).reduce((n, g) => n + g.certs.length, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.75, ease: EASE }}
      className="mb-20"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="section-label mb-4">Licenses &amp; Certifications</p>
          <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight">
            {CERTS.length} credentials. {ISSUER_COUNT} issuers.
          </h3>
        </div>
        <p className="text-sm text-text-secondary max-w-[340px] md:text-right leading-relaxed">
          From McKinsey and J.P. Morgan to Anthropic, AWS and Google. Continuous, deliberate upskilling.
        </p>
      </div>

      {/* Highlights */}
      <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10 -mx-6 px-6 sm:mx-0 sm:px-0 overflow-x-auto sm:overflow-visible snap-x snap-mandatory scroll-px-6 no-scrollbar">
        {HIGHLIGHTS.map((h, i) => (
          <motion.div
            key={h.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
            className="group relative overflow-hidden rounded-2xl border p-6 transition-transform duration-500 hover:-translate-y-1 min-w-[80%] sm:min-w-0 snap-start"
            style={{
              borderColor: `${h.accent}33`,
              background: `radial-gradient(120% 90% at 0% 0%, ${h.accent}1f, transparent 60%), rgba(12,12,16,0.85)`,
            }}
          >
            <div
              className="mb-5 flex h-10 w-10 items-center justify-center rounded-xl"
              style={{ background: `${h.accent}1a`, border: `1px solid ${h.accent}40`, color: h.accent }}
            >
              <h.icon className="h-[18px] w-[18px]" />
            </div>
            <p className="text-[9.5px] uppercase tracking-[0.22em] font-medium mb-2" style={{ color: h.accent }}>
              {h.kicker}
            </p>
            <p className="font-display text-lg font-medium mb-1.5 leading-tight">{h.title}</p>
            <p className="text-[12.5px] text-text-secondary leading-relaxed">{h.body}</p>
          </motion.div>
        ))}
      </div>

      {/* Domain filter */}
      <div className="mb-6 -mx-6 px-6 overflow-x-auto no-scrollbar">
        <div className="inline-flex gap-1 p-1 rounded-full border border-white/[0.08] bg-white/[0.025]">
          {DOMAINS.map((d) => (
            <button
              key={d.id}
              onClick={() => { setDomain(d.id); setAllGroups(false); }}
              aria-pressed={domain === d.id}
              className={`relative px-4 py-2 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors duration-300 ${
                domain === d.id ? 'text-black' : 'text-text-secondary hover:text-white'
              }`}
            >
              {domain === d.id && (
                <motion.span layoutId="cert-domain" className="absolute inset-0 rounded-full bg-white" transition={{ type: 'spring', stiffness: 380, damping: 32 }} />
              )}
              <span className="relative z-10 flex items-center gap-1.5">
                {d.label}
                <span className={`text-[10px] tabular-nums ${domain === d.id ? 'text-black/50' : 'text-white/30'}`}>{domainCount(d.id)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Issuer groups — masonry columns */}
      <div className="columns-1 md:columns-2 xl:columns-3 gap-4">
        <AnimatePresence initial={false}>
          {(isPhone && !allGroups ? groups.slice(0, 4) : groups).map((g) => (
            <GroupCard key={g.key} group={g} preview={isPhone ? 3 : PREVIEW} />
          ))}
        </AnimatePresence>
      </div>

      {isPhone && groups.length > 4 && (
        <div className="flex justify-center mt-2">
          <button
            onClick={() => setAllGroups((v) => !v)}
            className="flex items-center gap-2 px-6 py-3 rounded-full border border-white/[0.12] text-sm font-medium text-text-secondary hover:text-white transition-colors"
          >
            {allGroups ? 'Show fewer issuers' : `Show all ${groups.length} issuers`}
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${allGroups ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default Certifications;
